export type CoachEvidenceReference = {
  label: string;
  link: string | null;
  evidenceId?: string | null;
};

export type CoachEvidenceCanonicalIdentity = {
  sourceType:
    | "transcript_upload"
    | "post_meeting_reflection"
    | "assessment"
    | "shared_reflection"
    | "leader_artifact"
    | "practice_focus_suggestion";
  sourceId: string;
  leaderId: string;
};

export type CoachEvidenceItem = {
  id: string;
  link: string | null;
  source: string;
  subtype: string | null;
  meeting_id: string | null;
  canonicalIdentity: CoachEvidenceCanonicalIdentity;
};

export type CoachEvidenceResolution<T> =
  | { status: "resolved"; item: T }
  | { status: "ambiguous" }
  | { status: "unavailable" };

export function toCoachEvidenceLink(link: string | null): string | null {
  if (!link) return null;
  const transcriptAnalysisMatch = link.match(/\/transcript-analysis\/([^/?#]+)/);
  if (transcriptAnalysisMatch) {
    return link.replace(
      `/transcript-analysis/${transcriptAnalysisMatch[1]}`,
      `/coach/post-meeting/${transcriptAnalysisMatch[1]}`,
    );
  }
  return link;
}

export function dedupeEvidenceLinks<T extends { label: string; link: string | null; evidenceId?: string | null }>(entries: T[]) {
  const seen = new Set<string>();
  return entries.filter((entry) => {
    const key = `${entry.label}::${entry.link || ""}::${entry.evidenceId || ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function resolutionFromMatches<T>(matches: readonly T[]): CoachEvidenceResolution<T> {
  if (matches.length === 1) return { status: "resolved", item: matches[0] };
  if (matches.length > 1) return { status: "ambiguous" };
  return { status: "unavailable" };
}

function decodeUrlComponent(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function normalizeEvidenceLink(link: string): string | null {
  try {
    const coachLink = toCoachEvidenceLink(link) ?? link;
    const url = new URL(coachLink, "https://coach-veera.local");
    const normalizedParams = new URLSearchParams();
    Array.from(url.searchParams.entries())
      .sort(([leftKey, leftValue], [rightKey, rightValue]) => (
        leftKey.localeCompare(rightKey) || leftValue.localeCompare(rightValue)
      ))
      .forEach(([key, value]) => normalizedParams.append(key, value));
    const query = normalizedParams.toString();
    return `${decodeUrlComponent(url.pathname)}${query ? `?${decodeUrlComponent(query)}` : ""}`;
  } catch {
    return null;
  }
}

function sourceTypeForQueryType(value: string | null): CoachEvidenceCanonicalIdentity["sourceType"] | null {
  switch (value) {
    case "transcript": return "transcript_upload";
    case "reflection": return "post_meeting_reflection";
    case "shared-reflection": return "shared_reflection";
    case "artifact": return "leader_artifact";
    case "assessment": return "assessment";
    case "practice-focus": return "practice_focus_suggestion";
    default: return null;
  }
}

type ExactLinkResolution<T> =
  | { readonly kind: "none" }
  | { readonly kind: "meeting_constraint"; readonly meetingId: string }
  | { readonly kind: "result"; readonly result: CoachEvidenceResolution<T> };

function resolveExactLinkSelector<T extends CoachEvidenceItem>(
  evidenceItems: readonly T[],
  link: string,
): ExactLinkResolution<T> {
  let url: URL;
  try {
    url = new URL(toCoachEvidenceLink(link) ?? link, "https://coach-veera.local");
  } catch {
    return { kind: "result", result: { status: "unavailable" } };
  }

  const transcriptMatch = url.pathname.match(/^\/coach\/post-meeting\/([^/]+)$/);
  if (transcriptMatch) {
    const transcriptId = decodeUrlComponent(transcriptMatch[1]);
    return {
      kind: "result",
      result: resolutionFromMatches(evidenceItems.filter((entry) => (
        entry.canonicalIdentity.sourceType === "transcript_upload"
        && entry.canonicalIdentity.sourceId === transcriptId
      ))),
    };
  }

  if (url.searchParams.get("section") !== "evidence") return { kind: "none" };
  const hasType = url.searchParams.has("type");
  const hasId = url.searchParams.has("id");
  const hasMeetingId = url.searchParams.has("meetingId");
  if (!hasType && !hasId && !hasMeetingId) return { kind: "none" };
  if (!hasType && !hasId && hasMeetingId) {
    const meetingId = url.searchParams.get("meetingId");
    return meetingId
      ? { kind: "meeting_constraint", meetingId }
      : { kind: "result", result: { status: "unavailable" } };
  }
  // A type-only route is a collection/presentation link, not an exact source
  // selector. An independent exact evidenceId may therefore remain authoritative.
  if (hasType && !hasId && !hasMeetingId) return { kind: "none" };

  const sourceType = sourceTypeForQueryType(url.searchParams.get("type"));
  const sourceId = url.searchParams.get("id");
  const meetingId = url.searchParams.get("meetingId");
  if (!sourceType || !sourceId) {
    return { kind: "result", result: { status: "unavailable" } };
  }
  return {
    kind: "result",
    result: resolutionFromMatches(evidenceItems.filter((entry) => (
      entry.canonicalIdentity.sourceId === sourceId
      && entry.canonicalIdentity.sourceType === sourceType
      && (!meetingId || entry.meeting_id === meetingId)
    ))),
  };
}

function sameCanonicalEvidence(
  left: CoachEvidenceItem,
  right: CoachEvidenceItem,
) {
  return left.canonicalIdentity.sourceType === right.canonicalIdentity.sourceType
    && left.canonicalIdentity.sourceId === right.canonicalIdentity.sourceId
    && left.canonicalIdentity.leaderId === right.canonicalIdentity.leaderId;
}

function isGenericEvidenceCollectionLink(link: string) {
  try {
    const url = new URL(toCoachEvidenceLink(link) ?? link, "https://coach-veera.local");
    return url.searchParams.get("section") === "evidence"
      && url.searchParams.has("type")
      && !url.searchParams.has("id")
      && !url.searchParams.has("meetingId");
  } catch {
    return false;
  }
}

export function resolveCoachEvidenceReferenceResult<T extends CoachEvidenceItem>(
  evidenceItems: readonly T[],
  reference: CoachEvidenceReference,
): CoachEvidenceResolution<T> {
  const evidenceId = reference.evidenceId?.trim() || null;
  if (evidenceId) {
    let evidenceIdResult: CoachEvidenceResolution<T>;
    if (evidenceId.startsWith("meeting:")) {
      const meetingId = evidenceId.slice("meeting:".length);
      if (!meetingId) return { status: "unavailable" };
      evidenceIdResult = resolutionFromMatches(
        evidenceItems.filter((entry) => entry.meeting_id === meetingId),
      );
    } else {
      evidenceIdResult = resolutionFromMatches(
        evidenceItems.filter((entry) => entry.id === evidenceId),
      );
    }

    if (reference.link) {
      const exactLink = resolveExactLinkSelector(evidenceItems, reference.link);
      if (exactLink.kind === "meeting_constraint") {
        if (
          evidenceIdResult.status !== "resolved"
          || evidenceIdResult.item.meeting_id !== exactLink.meetingId
        ) {
          return { status: "unavailable" };
        }
        return evidenceIdResult;
      }
      if (exactLink.kind === "result") {
        if (
          evidenceIdResult.status !== "resolved"
          || exactLink.result.status !== "resolved"
          || !sameCanonicalEvidence(evidenceIdResult.item, exactLink.result.item)
        ) {
          return { status: "unavailable" };
        }
      }
    }
    return evidenceIdResult;
  }

  if (!reference.link) return { status: "unavailable" };
  const normalizedTarget = normalizeEvidenceLink(reference.link);
  if (!normalizedTarget) return { status: "unavailable" };

  const exactLink = resolveExactLinkSelector(evidenceItems, reference.link);
  if (exactLink.kind === "result") return exactLink.result;
  if (exactLink.kind === "meeting_constraint") return { status: "unavailable" };
  if (isGenericEvidenceCollectionLink(reference.link)) {
    return { status: "unavailable" };
  }

  const exactLinkMatches = evidenceItems.filter((entry) => (
    entry.link ? normalizeEvidenceLink(entry.link) === normalizedTarget : false
  ));
  if (exactLinkMatches.length > 0) return resolutionFromMatches(exactLinkMatches);

  return { status: "unavailable" };
}

export function resolveCoachEvidenceReference<T extends CoachEvidenceItem>(
  evidenceItems: readonly T[],
  reference: CoachEvidenceReference,
): T | null {
  const result = resolveCoachEvidenceReferenceResult(evidenceItems, reference);
  return result.status === "resolved" ? result.item : null;
}
